/**
 * HOOK: useGhostAI
 * Gestisce AI e movimento dei fantasmi con pattern stabili e leggibili.
 */

import { useState, useCallback } from "react";
import {
    Ghost,
    PacmanState,
    Direction,
    DifficultyPreset,
} from "../types/GameTypes";
import { GAME_CONFIG, GHOST_SPAWN_POSITIONS } from "../types/GameConstants";

const _GHOST_HOME = { x: 380, y: 220 };
const _DIRECTIONS: Direction[] = ["up", "down", "left", "right"];
const _SCATTER_TARGETS = [
    { x: 720, y: 60 },  // Blinky
    { x: 60, y: 60 },   // Pinky
    { x: 720, y: 560 }, // Inky
    { x: 60, y: 560 },  // Clyde
] as const;
const _PERSONALITY_SPEED = [1.12, 1.0, 1.08, 0.94] as const;

interface DifficultyProfile {
    scoreCap: number;
    baseSpeedScale: number;
    maxGhostSpeed: number;
    maxChaseBonus: number;
    maxScatterBonus: number;
    frightenedMinDuration: number;
    frightenedReduction: number;
    phaseCompression: number;
}

const _DIFFICULTY_PROFILES: Record<DifficultyPreset, DifficultyProfile> = {
    easy: {
        scoreCap: 4200,
        baseSpeedScale: 0.9,
        maxGhostSpeed: 1.95,
        maxChaseBonus: 0.2,
        maxScatterBonus: 0.12,
        frightenedMinDuration: 6500,
        frightenedReduction: 1500,
        phaseCompression: 0.75,
    },
    normal: {
        scoreCap: 3400,
        baseSpeedScale: 1,
        maxGhostSpeed: 2.08,
        maxChaseBonus: 0.32,
        maxScatterBonus: 0.22,
        frightenedMinDuration: 5600,
        frightenedReduction: 2400,
        phaseCompression: 1,
    },
    hard: {
        scoreCap: 2600,
        baseSpeedScale: 1.08,
        maxGhostSpeed: 2.24,
        maxChaseBonus: 0.46,
        maxScatterBonus: 0.28,
        frightenedMinDuration: 4700,
        frightenedReduction: 3200,
        phaseCompression: 1.35,
    },
};

const _distance = (ax: number, ay: number, bx: number, by: number): number =>
    Math.hypot(ax - bx, ay - by);

const _toTile = (value: number): number =>
    Math.floor(value / GAME_CONFIG.CELL_SIZE);

const _toWorld = (tile: number): number =>
    tile * GAME_CONFIG.CELL_SIZE + GAME_CONFIG.CELL_SIZE / 2;

const _clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value));

export function useGhostAI(
    pacman: PacmanState,
    isValidPosition: (x: number, y: number) => boolean,
    width: number,
    height: number,
    score: number,
    difficultyPreset: DifficultyPreset,
) {
    const [ghosts, setGhosts] = useState<Ghost[]>([]);
    const [frightenedTimer, setFrightenedTimer] = useState(0);
    const profile = _DIFFICULTY_PROFILES[difficultyPreset];
    const difficulty = _clamp(score / profile.scoreCap, 0, 1);
    const chaseBonus =
        profile.baseSpeedScale * (1 + difficulty * profile.maxChaseBonus);
    const scatterBonus =
        profile.baseSpeedScale * (1 + difficulty * profile.maxScatterBonus);
    const frightenedDuration = Math.round(
        _clamp(
            GAME_CONFIG.POWER_UP_DURATION - difficulty * profile.frightenedReduction,
            profile.frightenedMinDuration,
            GAME_CONFIG.POWER_UP_DURATION,
        ),
    );

    const getOppositeDirection = (dir: Direction): Direction => {
        switch (dir) {
            case "up":
                return "down";
            case "down":
                return "up";
            case "left":
                return "right";
            case "right":
                return "left";
        }
    };

    const getNewPosition = useCallback(
        (x: number, y: number, direction: Direction, speed: number) => {
            switch (direction) {
                case "up":
                    return { x, y: y - speed };
                case "down":
                    return { x, y: y + speed };
                case "left":
                    return { x: x - speed, y };
                case "right":
                    return { x: x + speed, y };
            }
        },
        [],
    );

    const normalizeX = useCallback(
        (x: number): number => {
            if (x < 0) return width - GAME_CONFIG.CELL_SIZE;
            if (x >= width) return GAME_CONFIG.CELL_SIZE;
            return x;
        },
        [width],
    );

    const getChaseTarget = useCallback(
        (index: number, ghost: Ghost, allGhosts: Ghost[]) => {
            const pacTileX = _toTile(pacman.x);
            const pacTileY = _toTile(pacman.y);
            const aheadTiles = 4;

            if (index === 0) {
                // Blinky: inseguimento diretto.
                return { x: pacman.x, y: pacman.y };
            }

            if (index === 1) {
                // Pinky: predice 4 tile avanti.
                const targetTileX =
                    pacTileX +
                    (pacman.direction === "right"
                        ? aheadTiles
                        : pacman.direction === "left"
                          ? -aheadTiles
                          : 0);
                const targetTileY =
                    pacTileY +
                    (pacman.direction === "down"
                        ? aheadTiles
                        : pacman.direction === "up"
                          ? -aheadTiles
                          : 0);
                return {
                    x: _clamp(_toWorld(targetTileX), 0, width),
                    y: _clamp(_toWorld(targetTileY), 0, height),
                };
            }

            if (index === 2) {
                // Inky: vettore da Blinky verso tile previsto.
                const blinky = allGhosts[0] || ghost;
                const targetTileX =
                    pacTileX +
                    (pacman.direction === "right"
                        ? 2
                        : pacman.direction === "left"
                          ? -2
                          : 0);
                const targetTileY =
                    pacTileY +
                    (pacman.direction === "down"
                        ? 2
                        : pacman.direction === "up"
                          ? -2
                          : 0);
                const intermediateX = _toWorld(targetTileX);
                const intermediateY = _toWorld(targetTileY);
                const vectorX = intermediateX - blinky.x;
                const vectorY = intermediateY - blinky.y;
                return {
                    x: _clamp(intermediateX + vectorX, 0, width),
                    y: _clamp(intermediateY + vectorY, 0, height),
                };
            }

            // Clyde: aggressivo da lontano, evasivo da vicino.
            const clydeDistance = _distance(ghost.x, ghost.y, pacman.x, pacman.y);
            if (clydeDistance > GAME_CONFIG.CELL_SIZE * 7) {
                return { x: pacman.x, y: pacman.y };
            }
            return _SCATTER_TARGETS[3];
        },
        [height, pacman.direction, pacman.x, pacman.y, width],
    );

    const getGhostMode = useCallback(
        (ghost: Ghost) => {
            if (ghost.mode === "eaten") return "eaten" as const;
            if (frightenedTimer > 0) return "frightened" as const;

            // Pattern classico chase/scatter a finestre temporali.
            const cycleMs = Date.now() % 28000;
            const compression = profile.phaseCompression;
            const firstScatterWindow = _clamp(
                7000 - Math.round(1800 * difficulty * compression),
                4300,
                7000,
            );
            const secondScatterStart = _clamp(
                14000 - Math.round(900 * difficulty * compression),
                12000,
                14000,
            );
            const secondScatterEnd = _clamp(
                19000 - Math.round(700 * difficulty * compression),
                17300,
                19000,
            );
            const finalScatterStart = _clamp(
                25000 - Math.round(1200 * difficulty * compression),
                22500,
                25000,
            );
            const scatterPhase =
                cycleMs < firstScatterWindow ||
                (cycleMs >= secondScatterStart && cycleMs < secondScatterEnd) ||
                cycleMs >= finalScatterStart;

            return scatterPhase ? ("scatter" as const) : ("chase" as const);
        },
        [difficulty, frightenedTimer, profile.phaseCompression],
    );

    const pickDirection = useCallback(
        (ghost: Ghost, targetX: number, targetY: number, frightened: boolean) => {
            const opposite = getOppositeDirection(ghost.direction);
            const currentForward = getNewPosition(
                ghost.x,
                ghost.y,
                ghost.direction,
                ghost.speed,
            );
            const isBlocked = !isValidPosition(currentForward.x, currentForward.y);

            const centerX =
                Math.floor(ghost.x / GAME_CONFIG.CELL_SIZE) * GAME_CONFIG.CELL_SIZE +
                GAME_CONFIG.CELL_SIZE / 2;
            const centerY =
                Math.floor(ghost.y / GAME_CONFIG.CELL_SIZE) * GAME_CONFIG.CELL_SIZE +
                GAME_CONFIG.CELL_SIZE / 2;
            const nearCenter =
                Math.abs(ghost.x - centerX) < ghost.speed + 0.35 &&
                Math.abs(ghost.y - centerY) < ghost.speed + 0.35;

            if (!nearCenter && !isBlocked) {
                return ghost.direction;
            }

            const validDirections = _DIRECTIONS.filter((dir) => {
                if (!isBlocked && dir === opposite) return false;
                const next = getNewPosition(ghost.x, ghost.y, dir, ghost.speed);
                return isValidPosition(next.x, next.y);
            });

            if (validDirections.length === 0) {
                return opposite;
            }

            let bestDirection = validDirections[0];
            let bestScore = frightened ? -Infinity : Infinity;

            validDirections.forEach((dir) => {
                const next = getNewPosition(ghost.x, ghost.y, dir, ghost.speed);
                const distanceToTarget =
                    Math.abs(next.x - targetX) + Math.abs(next.y - targetY);
                const jitter = frightened ? Math.random() * 14 : Math.random() * 2;
                const score = frightened
                    ? distanceToTarget + jitter
                    : distanceToTarget - jitter;

                if (frightened) {
                    if (score > bestScore) {
                        bestScore = score;
                        bestDirection = dir;
                    }
                } else if (score < bestScore) {
                    bestScore = score;
                    bestDirection = dir;
                }
            });

            return bestDirection;
        },
        [getNewPosition, isValidPosition],
    );

    const initializeGhosts = useCallback(() => {
        const newGhosts = GHOST_SPAWN_POSITIONS.map((spawn, index) => ({
            x: spawn.x,
            y: spawn.y,
            color: spawn.color,
            mode: "scatter" as const,
            targetX: _SCATTER_TARGETS[index].x,
            targetY: _SCATTER_TARGETS[index].y,
            direction: (index % 2 === 0 ? "left" : "right") as Direction,
            speed: GAME_CONFIG.GHOST_SPEED * _PERSONALITY_SPEED[index],
        }));
        setGhosts(newGhosts);
        setFrightenedTimer(0);
    }, []);

    const updateGhosts = useCallback(
        (deltaTime: number) => {
            if (frightenedTimer > 0) {
                setFrightenedTimer((prev) => Math.max(0, prev - deltaTime));
            }

            setGhosts((prevGhosts) =>
                prevGhosts.map((ghost, index) => {
                    const resolvedMode = getGhostMode(ghost);

                    const nextGhost: Ghost = {
                        ...ghost,
                        mode: resolvedMode,
                    };

                    if (resolvedMode === "eaten") {
                        nextGhost.targetX = _GHOST_HOME.x;
                        nextGhost.targetY = _GHOST_HOME.y;
                        nextGhost.speed = _clamp(
                            GAME_CONFIG.GHOST_SPEED *
                                (1.95 + difficulty * 0.25) *
                                profile.baseSpeedScale,
                            0.6,
                            profile.maxGhostSpeed,
                        );
                    } else if (resolvedMode === "frightened") {
                        nextGhost.targetX = _SCATTER_TARGETS[index].x;
                        nextGhost.targetY = _SCATTER_TARGETS[index].y;
                        nextGhost.speed = _clamp(
                            GAME_CONFIG.FRIGHTENED_SPEED *
                                (0.78 + index * 0.05 + difficulty * 0.1) *
                                profile.baseSpeedScale,
                            0.45,
                            profile.maxGhostSpeed,
                        );
                    } else if (resolvedMode === "scatter") {
                        nextGhost.targetX = _SCATTER_TARGETS[index].x;
                        nextGhost.targetY = _SCATTER_TARGETS[index].y;
                        nextGhost.speed = _clamp(
                            GAME_CONFIG.GHOST_SPEED *
                                _PERSONALITY_SPEED[index] *
                                0.82 *
                                scatterBonus,
                            0.55,
                            profile.maxGhostSpeed,
                        );
                    } else {
                        const chaseTarget = getChaseTarget(index, ghost, prevGhosts);
                        nextGhost.targetX = chaseTarget.x;
                        nextGhost.targetY = chaseTarget.y;
                        nextGhost.speed = _clamp(
                            GAME_CONFIG.GHOST_SPEED *
                                _PERSONALITY_SPEED[index] *
                                chaseBonus,
                            0.65,
                            profile.maxGhostSpeed,
                        );
                    }

                    nextGhost.direction = pickDirection(
                        nextGhost,
                        nextGhost.targetX,
                        nextGhost.targetY,
                        resolvedMode === "frightened",
                    );

                    const moved = getNewPosition(
                        nextGhost.x,
                        nextGhost.y,
                        nextGhost.direction,
                        nextGhost.speed,
                    );
                    const wrappedX = normalizeX(moved.x);
                    const candidateX =
                        wrappedX !== moved.x ? wrappedX : moved.x;

                    if (isValidPosition(candidateX, moved.y)) {
                        nextGhost.x = candidateX;
                        nextGhost.y = moved.y;
                    }

                    if (
                        resolvedMode === "eaten" &&
                        _distance(nextGhost.x, nextGhost.y, _GHOST_HOME.x, _GHOST_HOME.y) <
                            GAME_CONFIG.CELL_SIZE * 0.6
                    ) {
                        nextGhost.mode = "scatter";
                    }

                    return nextGhost;
                }),
            );
        },
        [
            frightenedTimer,
            difficulty,
            profile.baseSpeedScale,
            profile.maxGhostSpeed,
            getChaseTarget,
            getGhostMode,
            getNewPosition,
            isValidPosition,
            normalizeX,
            pickDirection,
            chaseBonus,
            scatterBonus,
        ],
    );

    const activateFrightenedMode = useCallback(() => {
        setFrightenedTimer(frightenedDuration);
        setGhosts((prev) =>
            prev.map((ghost) =>
                ghost.mode === "eaten"
                    ? ghost
                    : {
                          ...ghost,
                          mode: "frightened" as const,
                          speed: GAME_CONFIG.FRIGHTENED_SPEED,
                      },
            ),
        );
    }, [frightenedDuration]);

    const eatGhost = useCallback((ghostIndex: number) => {
        setGhosts((prev) =>
            prev.map((ghost, index) =>
                index === ghostIndex
                    ? {
                          ...ghost,
                          mode: "eaten" as const,
                          targetX: _GHOST_HOME.x,
                          targetY: _GHOST_HOME.y,
                      }
                    : ghost,
            ),
        );
    }, []);

    return {
        ghosts,
        frightenedTimer,
        initializeGhosts,
        updateGhosts,
        activateFrightenedMode,
        eatGhost,
        setFrightenedTimer,
    };
}
